import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { addToCart } from '../../store/cartSlice';
import { addToWishlist, removeFromWishlist } from '../../store/wishlistSlice';
import { setPreviousRoute } from '../../store/navigationSlice';
import { showShopToast } from '../../utils/shopToast';
import './BrandPage.css';

import ShopNav from '../../components/shopNav/ShopNav';
import Footer from '../../components/footer/footer';
import NavCategorias from '../../components/navbar/NavCategorias';
import ProductFilter from '../../components/filters/ProductFilter';
import ServiceHighlights from '../../components/servicehighlights/ServiceHighlights';
import ProductImageGallery from '../../components/shop/ProductImageGallery';

// ✅ Variable de entorno
const API_URL = import.meta.env.VITE_API_URL;

// ✅ Función reutilizable para formatear precios en COP
const formatCOP = (value) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const BrandPage = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 12;
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [filters, setFilters] = useState({
    search: '',
    minPrice: '',
    maxPrice: '',
    sort: ''
  });

  const wishlistItems = useSelector(state => state.wishlist.items || []);
  const isInWishlist = id => wishlistItems.some(item => item.id === id);

  useEffect(() => {
    dispatch(setPreviousRoute(window.location.pathname));
  }, [dispatch, brandId]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/stock/brand/${brandId}`);
        setProducts(response.data);
      } catch (err) {
        setError('Error al cargar los productos. Intente nuevamente.');
        console.error('Error fetching brand products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [brandId]);

  const filteredProducts = useMemo(() => {
    let result = products.filter(product => {
      const nameMatch = product.name.toLowerCase().includes(filters.search.toLowerCase());
      const minPriceMatch = filters.minPrice === '' || product.price >= parseFloat(filters.minPrice);
      const maxPriceMatch = filters.maxPrice === '' || product.price <= parseFloat(filters.maxPrice);
      return nameMatch && minPriceMatch && maxPriceMatch;
    });

    switch (filters.sort) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name-asc':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        result.sort((a, b) => b.name.localeCompare(a.name));
        break;
    }

    return result;
  }, [products, filters]);

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / productsPerPage));
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  const paginate = pageNumber => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    dispatch(addToCart(product));
    showShopToast(`${product.name} agregado al carrito`, 'success');
  };

  const handleToggleWishlist = (e, product) => {
    e.stopPropagation();
    if (isInWishlist(product.id)) {
      dispatch(removeFromWishlist(product.id));
      setToastMessage(`${product.name} eliminado de la lista de deseos`);
    } else {
      dispatch(addToWishlist(product));
      setToastMessage(`${product.name} agregado a la lista de deseos`);
    }
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleProductDetails = (id) => {
    navigate(`/producto/${id}`);
  };

  const brandName = useMemo(() => brandId.replace(/-/g, ' '), [brandId]);

  return (
    <div className="brand-page">
      <ShopNav />
      <NavCategorias />

      <main className="brand-page-main">
        <section className="brand-page-hero brand-page-elevated-wrap brand-page-elevated-wrap--hero">
          <div className="container brand-page-hero-inner">
            <div className="brand-page-hero-copy">
              <h1 className="display-5 animate__animated animate__fadeIn">{brandName}</h1>
              <p className="lead animate__animated animate__fadeIn">
                Explora el catalogo oficial de esta marca con seleccion especializada para estudio, escenario y produccion.
              </p>
            </div>
            <div className="brand-page-hero-logo-wrap animate__animated animate__fadeIn" aria-hidden="true">
              <img src="/images/VPA.png" alt="Vintage Pro Audio" className="brand-page-hero-logo" />
            </div>
          </div>
        </section>

        <section className="brand-page-elevated-wrap">
          <ServiceHighlights />
        </section>

        <section className="brand-page-elevated-wrap brand-page-filter-wrap">
          <ProductFilter filters={filters} setFilters={setFilters} />
        </section>

        <section className="container brand-page-results">
          {loading ? (
            <div className="brand-page-state text-center">Cargando productos...</div>
          ) : error ? (
            <div className="alert alert-danger text-center">{error}</div>
          ) : (
            <>
              <div className="brand-page-results-head">
                <h2 className="brand-page-results-title">Productos de {brandName}</h2>
                <p className="brand-page-results-meta">{filteredProducts.length} producto(s) disponible(s)</p>
              </div>

              <div className="row g-4 brand-products-grid">
                {currentProducts.map(product => {
                  let pdfs = {};

                  try {
                    pdfs = product.pdfUrl ? JSON.parse(product.pdfUrl) : {};
                  } catch (e) {
                    console.warn(`⚠️ No se pudo parsear pdfUrl del producto ID ${product.id}`);
                  }

                  return (
                    <div key={product.id} className="col-12 col-sm-6 col-lg-4 brand-product-col" onClick={() => handleProductDetails(product.id)}>
                      <div className="card shadow-sm h-100 brand-product-card">
                        <div className="card-body d-flex flex-column">
                          <div className="brand-card-wishlist" onClick={(e) => handleToggleWishlist(e, product)} style={{ zIndex: 10 }}>
                            <i className={`bi bi-heart${isInWishlist(product.id) ? '-fill text-danger' : ''}`} style={{ fontSize: '1.5rem' }}></i>
                          </div>

                          <div onClick={(e) => e.stopPropagation()} className="image-gallery-wrapper brand-card-media mb-3">
                            <ProductImageGallery
                              imageUrl={product.imageUrl}
                              productName={product.name}
                              showPlayButton={false}
                              showFullscreenButton={false}
                              showThumbnails={false}
                              autoPlay
                            />
                          </div>

                          <div className="brand-card-meta">
                            <span className="brand-card-chip">{brandName}</span>
                            <span className="brand-card-sku">Ref. {product.id}</span>
                          </div>

                          <h5 className="card-title">{product.name}</h5>
                          <p className="card-text">{product.description}</p>

                          <div className="brand-card-pricing">
                            <p className="fw-bold text-success">{formatCOP(product.priceCOP)}</p>
                          </div>

                          <div className="brand-card-actions mt-auto" onClick={(e) => e.stopPropagation()}>
                            {pdfs.pdf1 && (
                              <a href={pdfs.pdf1} target="_blank" rel="noopener noreferrer" className="btn btn-primary w-100">Descargar Especificaciones</a>
                            )}

                            <button className="btn btn-success w-100" onClick={(e) => handleAddToCart(e, product)}>
                              Agregar al carrito
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <nav className="mt-4">
                <ul className="pagination justify-content-center brand-pagination">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(1)}>&laquo;</button>
                  </li>
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(currentPage - 1)}>&lsaquo;</button>
                  </li>
                  {[...Array(totalPages).keys()].map(n => (
                    <li key={n + 1} className={`page-item ${currentPage === n + 1 ? 'active' : ''}`}>
                      <button onClick={() => paginate(n + 1)} className="page-link">{n + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(currentPage + 1)}>&rsaquo;</button>
                  </li>
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => paginate(totalPages)}>&raquo;</button>
                  </li>
                </ul>
              </nav>
            </>
          )}
        </section>

        <div className="toast-container position-fixed bottom-0 end-0 p-3 shop-toast-container" style={{ zIndex: 9999 }}>
          <div className={`toast align-items-center text-bg-success border-0 ${showToast ? 'show' : 'hide'}`} role="alert">
            <div className="d-flex">
              <div className="toast-body">{toastMessage}</div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setShowToast(false)}></button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BrandPage;
